import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {ChevronLeft} from '@material-ui/icons';
import {actions} from '../../../store';
import {ReduxState} from '../../../store/types';
import {Permission} from '../../../store/types/Permission';
import DetailsWrapper from '../../../components/DetailsWraper/DetailsWrapper';
import PermissionPlaceholder from '../../../components/PermissionProvider/PermissionPlaceholder';
import Loader from '../../../components/Loader/Loader';
import CustomizedIconButton from '../../../components/IconButton/IconButton';
import Switch from '../../../components/Switch/Switch';
import {YesNo} from '../../../store/types/CallScreening';
import history from '../../../history';
import {Routes} from '../../../routes/routes';
import {generateTabs} from '../../../utils/generateTabs';
import {useRawPermissions} from '../../../hooks/usePermissions';
import {getTabNumber} from '../../../utils/getTabNumber';
import {AccountDetailsProps, useHeaderStyles, useStyles} from './AccountDetails.utils';
import {formatLastUsage, getBillStatusLabel, getBillStatusVariant} from '../AccountsList/AccountsList.utils';
import GeneralTabWrapper from './tabs/GeneralTabWrapper';
import PlanTab from './tabs/PlanTab';
import TransactionsTab from './tabs/TransactionsTab';
import usePageTitle from '../../../hooks/usePageTitle';
import {MainProductType} from '../../../components/Extensions/MainProductSelectFiled/utils';
import ErrorNotification from '../../../components/TabErrors/ErrorNotification';
import {TabInterface} from '../../../components/Tabs/Tabs.utils';
import NotFound from '../../NotFound/NotFound';
import {EXTENSION_NOT_FOUND_BY_ID_API_ERROR} from '../../../store/sagas/extensions/extensions/saga';

const AccountDetails: React.VFC<AccountDetailsProps> = ({id}) => {
    usePageTitle();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const headerClasses = useHeaderStyles();
    const permissions = useRawPermissions();

    const [isDirty, setIsDirty] = useState(false);
    const [tabIndex, setTabIndex] = useState(getTabNumber([0, 1, 2]));

    const saveClicked = useSelector(
        (state: ReduxState) => state.tabs?.saveForms || false,
    );

    const {accountDetails} = useSelector((state: ReduxState) => state.accounts);
    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format || '',
    );
    const isLoading = accountDetails?.isFormDataLoading;
    const isSaving = accountDetails?.isFormSending;

    const formData = useRef<{ init: Record<string, any>; updated: Record<string, any> }>({
        init: {},
        updated: {},
    });

    const formsSubmitFuncRef = useRef<Record<string, (() => void) | undefined>>({
        general: undefined,
        plan: undefined,
    });

    const formsErrorsValidationFuncRef = useRef<Record<string, (() => boolean) | undefined>>({
        general: undefined,
        plan: undefined,
    });

    const formsIsDirtyRef = useRef<Record<string, boolean>>({
        general: false,
        plan: false,
    });

    useEffect(() => {
        dispatch(actions.getAccountDetails.request({id}));
    }, [id]);

    const handleBack = () => {
        history.push(Routes.Accounts);
    };

    const checkAllDirtyState = useCallback(() => {
        const isAnyFormDirty = Object.values(formsIsDirtyRef.current).some(Boolean);
        setIsDirty(isAnyFormDirty);
    }, []);

    const setIsDirtyFunc = useCallback((
        funcName: string,
        dirty: boolean,
    ) => {
        formsIsDirtyRef.current[funcName] = dirty;
        checkAllDirtyState();
    }, [checkAllDirtyState]);

    const setSubmitFunc = useCallback((
        funcName: string,
        func: () => void,
    ) => {
        formsSubmitFuncRef.current[funcName] = func;
    }, []);

    const setFormIsValidFunc = useCallback((
        funcName: string,
        func: () => boolean,
    ) => {
        formsErrorsValidationFuncRef.current[funcName] = func;
    }, []);

    const handleDataFormUpdate = useCallback((form: any) => {
        formData.current = {
            init: {...formData.current.init},
            updated: {...formData.current.updated, ...form},
        };
    }, []);

    const handleDataInitFormUpdate = useCallback((form: any) => {
        formData.current = {
            init: {...formData.current.init, ...form},
            updated: {...formData.current.updated},
        };
    }, []);

    const submitAllForms = useCallback(() => {
        Object.values(formsSubmitFuncRef.current).forEach((fn) => fn?.());
    }, []);

    const tabsNameResolver: Record<string, string> = {
        general: t('screens:accounts.tabGeneral'),
        plan: t('screens:accounts.tabPlan'),
    };

    const markTabAsHaveError = (tabName: string, tabIdx: number, setTabError: boolean) => {
        dispatch(actions.setErrorInTab({name: tabName, index: tabIdx, markAsInvalid: setTabError}));
    };

    const areAllFormsValid = useCallback(() => {
        let formsAreValid = true;
        Object.keys(formsErrorsValidationFuncRef.current).forEach((key) => {
            let setTabError;
            if (
                formsErrorsValidationFuncRef.current[key] !== undefined &&
                !formsErrorsValidationFuncRef.current[key]?.()
            ) {
                formsAreValid = false;
                setTabError = true;
            } else {
                setTabError = false;
            }
            const name = tabsNameResolver[key];
            if (name) {
                markTabAsHaveError(name, tabNames.indexOf(name), setTabError);
            }
        });
        return formsAreValid;
    }, []);

    const handleSave = useCallback(() => {
        submitAllForms();

        let formsAreValid = true;
        setTimeout(() => {
            formsAreValid = areAllFormsValid();
        }, 0);

        setTimeout(() => {
            if (formsAreValid) {
                setTimeout(() => {
                    if (accountDetails?.i_account) {
                        dispatch(
                            actions.editAccount.request({
                                i_account: accountDetails.i_account,
                                initialValues: formData.current.init,
                                changedValues: formData.current.updated,
                                successCallback: () => {
                                    dispatch(actions.getAccountDetails.request({id: accountDetails.id ?? id}));
                                },
                            }),
                        );
                    }
                    dispatch(actions.saveForms({visible: false}));
                }, 0);
            } else {
                dispatch(actions.saveForms({visible: true}));
            }
        }, 1000);
    }, [formData, accountDetails, id, submitAllForms, areAllFormsValid]);

    const tabProps = useMemo(() => ({
        handleSubmitForm: handleDataFormUpdate,
        handleInitDataChange: handleDataInitFormUpdate,
        handleDirtyChange: setIsDirtyFunc,
        handleSetSubmitFunc: setSubmitFunc,
        handleSetIsValidFunc: setFormIsValidFunc,
    } as TabInterface), [
        handleDataFormUpdate,
        handleDataInitFormUpdate,
        setIsDirtyFunc,
        setSubmitFunc,
        setFormIsValidFunc,
    ]);

    const onTabChange = useCallback((_: React.ChangeEvent<{}>, index: any) => {
        areAllFormsValid();
        dispatch(actions.saveForms({visible: false}));
        setTabIndex(index);
    }, [areAllFormsValid]);

    const {tabNames, tabs, tabsIcons} = generateTabs(
        [
            {
                title: t('screens:accounts.tabGeneral'),
                permission: Permission.Accounts.AccountDetails.General.value,
                tab: (
                    <GeneralTabWrapper
                        {...tabProps}
                        isActive={tabIndex === 0}
                        tabName={t('screens:accounts.tabGeneral')}
                        tabIndex={0}
                    />
                ),
            },
            {
                title: t('screens:accounts.tabPlan'),
                permission: Permission.Accounts.AccountDetails.Plan.value,
                tab: (
                    <PlanTab
                        {...tabProps}
                        isActive={tabIndex === 1}
                        tabName={t('screens:accounts.tabPlan')}
                        tabIndex={1}
                        productType={MainProductType.Accounts}
                    />
                ),
            },
            {
                title: t('screens:accounts.tabTransactions'),
                permission: Permission.Accounts.AccountDetails.Transactions.value,
                tab: <TransactionsTab iAccount={accountDetails?.i_account ?? 0} />,
            },
        ],
        permissions,
    );

    const billStatus = accountDetails?.bill_status;
    const activationDate = accountDetails?.activation_date;
    const statusLabel = getBillStatusLabel(billStatus, t, activationDate);
    const variant = getBillStatusVariant(billStatus, activationDate);
    const isBlocked = accountDetails?.blocked === 'Y';
    const dotClass = {
        active: headerClasses.statusDotActive,
        notYetActive: headerClasses.statusDotNotYetActive,
        suspended: headerClasses.statusDotSuspended,
        inactive: headerClasses.statusDotInactive,
    }[variant];
    const labelClass = {
        active: headerClasses.statusActive,
        notYetActive: headerClasses.statusNotYetActive,
        suspended: headerClasses.statusSuspended,
        inactive: headerClasses.statusInactive,
    }[variant];
    const lastUsed = billStatus === 'I'
        ? ''
        : formatLastUsage(accountDetails?.last_usage, activationDate, t, userDateFormat);

    const handleChangeStatusClick = useCallback(() => {
        if (accountDetails?.i_account) {
            dispatch(
                actions.setExtensionStatus.request({
                    account_info: {
                        i_account: accountDetails.i_account.toString(),
                        blocked: isBlocked ? YesNo.No : YesNo.Yes,
                    },
                    successMessages: {
                        enabled: 'screens:accounts.accountEnabled',
                        disabled: 'screens:accounts.accountDisabled',
                    },
                }),
            );
        }
    }, [isBlocked, accountDetails?.i_account]);

    const header = (
        <div className={headerClasses.headerContainer}>
            <div className={headerClasses.headerTop}>
                <div className={headerClasses.leftContainer}>
                    <CustomizedIconButton
                        className={headerClasses.backButton}
                        onClick={handleBack}
                        skipPermission
                    >
                        <ChevronLeft />
                    </CustomizedIconButton>
                    {t('screens:accounts.account')}
                    <span className={headerClasses.accountId}>{accountDetails?.id ?? id}</span>
                </div>

                <div className={headerClasses.rightContainer}>
                    {accountDetails && (
                        <div className={headerClasses.statusCell}>
                            <div className={headerClasses.statusBadge}>
                                <span className={`${headerClasses.statusDot} ${dotClass}`} />
                                <span className={`${headerClasses.statusText} ${labelClass}`}>{statusLabel}</span>
                            </div>
                            {lastUsed && (
                                <span className={headerClasses.lastUsed}>{lastUsed}</span>
                            )}
                        </div>
                    )}
                    <div className={headerClasses.statusSwitcher}>
                        <Switch
                            checked={!isBlocked}
                            onChange={handleChangeStatusClick}
                            dataQa="account-details-status-switcher"
                            dataTestId="account-details-status-switcher"
                            className={headerClasses.switch}
                        />
                        <span>
                            {!isBlocked
                                ? t('extensions.enabled')
                                : t('extensions.disabled')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    if (accountDetails?.apiError?.faultcode === EXTENSION_NOT_FOUND_BY_ID_API_ERROR) {
        return <NotFound />;
    }

    if (isLoading) {
        return (
            <div className={classes.loaderContainer}>
                <Loader dataQa="account-details-loader" />
            </div>
        );
    }

    return (
        <PermissionPlaceholder permission={Permission.Accounts.AccountDetails.value}>
            <div className={classes.mainContainer}>
                {isSaving && (
                    <Loader
                        dataQa="account-details-saving-loader"
                        absolutePosition
                    />
                )}
                <DetailsWrapper
                    top={header}
                    tabs={tabs}
                    tabsNames={tabNames}
                    tabsIcons={tabsIcons}
                    showActionButtons
                    saveEnable={isDirty && !isSaving}
                    onSavePress={handleSave}
                    onBackPress={handleBack}
                    onTabChange={onTabChange}
                    defaultTabIndex={tabIndex}
                    actionsCustomClass={classes.actionsPanel}

                >
                    <ErrorNotification tabName={tabNames[tabIndex]} isSubmitting={saveClicked}/>
                </DetailsWrapper>
            </div>
        </PermissionPlaceholder>
    );
};

export default AccountDetails;
